import type { ReactNode } from 'react'
import uniqueId from 'lodash/uniqueId'

import { AbortError, isAbortError } from '@app/errors/AbortError'

import type { AnalyticsData } from '@app/packages/analytics/declaration'
import { asError } from '@app/packages/asError/asError'
import type { Result } from '@app/packages/Result/Result'
import { resultError, resultOk } from '@app/packages/Result/Result'

import type { ThunkAction } from '@app/store/thunk'
import { createThunk } from '@app/store/thunk'

import type { AskActionName, AskName } from '@app/components/ActionButtonsModal/ActionButtonsModal'
import type { ActionButtonsType } from '@app/components/ActionButtonsModal/ActionButtonsType'
import type { ConfirmName } from '@app/components/Confirm/events'
import { assertMountPush } from '@app/components/Mount/actions'

import { withProgress, withProgressAction } from './initial'

export function showError(options: { error: Error | Error[]; onClose?: () => unknown }) {
  return createThunk(async dispatch => {
    console.error('showError:', options.error)
    const { promise, resolve } = Promise.withResolvers<void>()
    await dispatch(
      assertMountPush(
        import('@app/components/ErrorModal/ErrorModal').then(m => m.ErrorModal),
        { props: { id: uniqueId(), ...options }, onClose: resolve }
      )
    )
    return promise
  })
}

export function confirm({
  name,
  data,
  title,
  content,
  closeButtonContent,
}: {
  name: ConfirmName
  data?: AnalyticsData
  title?: ReactNode
  content: ReactNode
  closeButtonContent?: ReactNode
}) {
  return createThunk(async dispatch => {
    const { promise, resolve } = Promise.withResolvers<boolean>()
    await dispatch(
      assertMountPush(
        import('@app/components/Confirm/Confirm').then(m => m.Confirm),
        { props: { name, data, title, content, closeButtonContent, onClose: resolve } }
      )
    )
    return promise
  })
}

export function ask<T>({
  name,
  title,
  text,
  actions,
}: {
  name: AskName
  title?: ReactNode
  text?: ReactNode
  actions: {
    name: AskActionName
    type?: ActionButtonsType
    text: string
    onClick: () => T
  }[]
}) {
  return createThunk(async dispatch => {
    const { promise, resolve, reject } = Promise.withResolvers<T>()
    let resolved = false
    const close = await dispatch(
      assertMountPush(
        import('@app/components/ActionButtonsModal/ActionButtonsModal').then(m => m.ActionButtonsModal),
        {
          props: {
            name,
            title,
            text,
            actions: actions.map(a => {
              return {
                ...a,
                onClick: async () => {
                  const res = await a.onClick()
                  resolved = true
                  await close()
                  resolve(res)
                },
              }
            }),
          },
          onClose: () => {
            if (!resolved) {
              reject(new AbortError('Modal closed without action'))
            }
          },
        }
      )
    )
    return promise
  })
}

export const handleCallback = <T>(callback: () => T, blocking?: boolean) => {
  return createThunk(async (dispatch): Promise<Result<Awaited<T>>> => {
    try {
      const result = await dispatch(withProgress(callback(), blocking))
      return resultOk(result)
    } catch (e) {
      if (!isAbortError(e)) {
        void dispatch(showError({ error: asError(e) }))
      }
      return resultError(asError(e))
    }
  })
}

export const handleCallbackAction = <T>(action: ThunkAction<T>, blocking?: boolean) => {
  return createThunk(async (dispatch): Promise<Result<Awaited<T>>> => {
    try {
      const result = await dispatch(withProgressAction(action, blocking))
      return resultOk(result)
    } catch (e) {
      if (!isAbortError(e)) {
        void dispatch(showError({ error: asError(e) }))
      }
      return resultError(asError(e))
    }
  })
}
